import { Search } from "lucide-react";
import { useTags, useUsers } from "@/context/AuthContext";
import { initials, listTime } from "@/lib/format";

const TITLES = { all: "Tutte le chat", mine: "Le mie chat", unassigned: "Non assegnate", closed: "Chiuse" };

const ChatItem = ({ c, active, onSelect, agent, tagColors }) => (
  <button
    data-testid={`chat-item-${c.id}`}
    onClick={() => onSelect(c.id)}
    className={`w-full text-left px-4 py-3 flex gap-3 border-b border-slate-100 transition-colors ${active ? "bg-white shadow-[inset_3px_0_0_#25D366]" : "hover:bg-white/70"}`}
  >
    <div className="w-10 h-10 shrink-0 rounded-full bg-slate-200 text-slate-600 grid place-items-center text-xs font-bold">{initials(c.name || "#")}</div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span data-testid="chat-item-customer-name" className={`text-sm truncate ${c.unread ? "font-bold text-slate-900" : "font-semibold text-slate-800"}`}>{c.name || c.phone}</span>
        <span className="ml-auto text-[11px] text-slate-400 shrink-0">{listTime(c.last_message_at)}</span>
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-xs text-slate-500 truncate flex-1">{c.last_message}</span>
        {c.unread > 0 && <span data-testid="chat-item-unread" className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-[#25D366] text-[10px] font-bold text-white grid place-items-center">{c.unread}</span>}
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        <span data-testid="chat-item-agent-badge" className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={agent ? { background: `${agent.color}1A`, color: agent.color } : { background: "#FFEDD5", color: "#C2410C" }}>
          {agent ? agent.name : "Non assegnata"}
        </span>
        {c.tags.map((t) => (
          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 flex items-center gap-1">
            <i className="w-1.5 h-1.5 rounded-full" style={{ background: tagColors[t] || "#94A3B8" }} />{t}
          </span>
        ))}
      </div>
    </div>
  </button>
);

const MOBILE_FILTERS = [["all", "Tutte"], ["mine", "Mie"], ["unassigned", "Non assegnate"], ["closed", "Chiuse"]];

const MobileFilters = ({ filter, setFilter, tag, setTag, counts, tags }) => (
  <div className="lg:hidden bg-white border-b border-slate-200 px-3 py-2 space-y-2" data-testid="mobile-inbox-filters">
    <div className="flex gap-1.5 overflow-x-auto thin-scroll pb-0.5">
      {MOBILE_FILTERS.map(([id, label]) => (
        <button key={id} data-testid={`mobile-filter-${id}`} onClick={() => setFilter(id)}
          className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${filter === id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
          {label} <span className={filter === id ? "text-[#25D366]" : "text-slate-400"}>{counts?.[id] ?? 0}</span>
        </button>
      ))}
    </div>
    <div className="flex gap-1.5 overflow-x-auto thin-scroll pb-0.5">
      {tags.map((t) => (
        <button key={t.id} data-testid={`mobile-tag-${t.name.toLowerCase()}`} onClick={() => setTag(tag === t.name ? null : t.name)}
          className={`shrink-0 flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${tag === t.name ? "border-slate-900 bg-slate-50 text-slate-900" : "border-slate-200 text-slate-500"}`}>
          <i className="w-2 h-2 rounded-full" style={{ background: t.color }} />{t.name}
        </button>
      ))}
    </div>
  </div>
);

export const ChatList = ({ items, selectedId, onSelect, search, setSearch, filter, setFilter, tag, setTag, counts, className = "flex" }) => {
  const { data: users = [] } = useUsers();
  const { data: tags = [] } = useTags();
  const byId = Object.fromEntries(users.map((u) => [u.id, u]));
  const tagColors = Object.fromEntries(tags.map((t) => [t.name, t.color]));
  return (
    <section className={`w-full md:w-80 shrink-0 border-r border-slate-200 bg-slate-50 flex-col ${className}`} data-testid="chat-list">
      <div className="h-16 px-4 flex items-center border-b border-slate-200 bg-white gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="chat-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nome, numero, azienda…" className="w-full h-9 pl-9 pr-3 text-sm bg-slate-100 rounded-lg outline-none focus:ring-2 focus:ring-[#25D366]/40 focus:bg-white transition-colors" />
        </div>
      </div>
      <MobileFilters filter={filter} setFilter={setFilter} tag={tag} setTag={setTag} counts={counts} tags={tags} />
      <div className="px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{TITLES[filter]} · {items.length}</div>
      <div className="flex-1 overflow-y-auto thin-scroll">
        {items.length === 0 && <p data-testid="chat-list-empty" className="text-sm text-slate-400 text-center py-12">Nessuna conversazione</p>}
        {items.map((c) => <ChatItem key={c.id} c={c} active={c.id === selectedId} onSelect={onSelect} agent={byId[c.assigned_to]} tagColors={tagColors} />)}
      </div>
    </section>
  );
};
